// Social Media AI Service - Content Suggestions & Hashtag Generator
import { supabase } from '@/config/supabase';

interface ContentSuggestion {
  id: string;
  content: string;
  platforms: string[];
  tone: 'professional' | 'casual' | 'enthusiastic' | 'informative' | 'humorous';
  category: string;
  hashtags: string[];
  emojis: string[];
  engagement_score: number;
  best_time_to_post: string;
  character_count: number;
  hooks: string[];
  cta_suggestions: string[];
}

interface HashtagSuggestion {
  tag: string;
  relevance_score: number;
  popularity: 'high' | 'medium' | 'low' | 'niche';
  trending: boolean;
  competition_level: number;
  estimated_reach: number;
  related_tags: string[];
}

interface ContentAnalysis {
  sentiment: 'positive' | 'negative' | 'neutral';
  readability_score: number;
  engagement_prediction: number;
  improvements: string[];
  keywords: string[];
}

interface TrendingTopic {
  topic: string;
  volume: number;
  growth_rate: number;
  related_hashtags: string[];
  content_ideas: string[];
}

export class SocialMediaAIService {
  
  // Baza wiedzy dla różnych branż
  private industryKeywords: Record<string, string[]> = {
    'plexi': ['pleksi', 'pmma', 'akryl', 'tworzywa', 'plastik', 'poliwęglan', 'ekspozytory', 'standy', 'reklama', 'display'],
    'produkcja': ['CNC', 'frezowanie', 'cięcie', 'gięcie', 'klejenie', 'polerowanie', 'grawerowanie', 'druk UV', 'termoformowanie'],
    'b2b': ['biznes', 'współpraca', 'zamówienia', 'realizacje', 'projekty', 'klienci', 'partnerzy', 'dostawa', 'oferta'],
    'marketing': ['promocja', 'nowość', 'oferta specjalna', 'rabat', 'wyprzedaż', 'premiera', 'wydarzenie', 'konkurs'],
    'edukacja': ['poradnik', 'tutorial', 'jak', 'dlaczego', 'co to jest', 'przewodnik', 'wskazówki', 'porady', 'FAQ']
  };

  // Szablony treści dla różnych celów
  private contentTemplates = {
    product_launch: [
      "🎯 Nowość w ofercie! {product_name}\n\n✨ {benefit_1}\n💎 {benefit_2}\n🚀 {benefit_3}\n\n{cta}",
      "Przedstawiamy {product_name} - {tagline}\n\n{description}\n\n✅ {feature_1}\n✅ {feature_2}\n✅ {feature_3}\n\n{cta}",
      "🔥 {product_name} już dostępny!\n\n{hook}\n\n{description}\n\n💼 Idealne dla: {target_audience}\n\n{cta}"
    ],
    case_study: [
      "💼 Realizacja dla {client}\n\n🎯 Wyzwanie: {challenge}\n💡 Rozwiązanie: {solution}\n✨ Efekt: {result}\n\n{cta}",
      "Success Story 🏆\n\n{client} potrzebował {need}.\n\nDostarczyliśmy {solution}.\n\nRezultat? {result}\n\n{cta}",
      "Od pomysłu do realizacji 🚀\n\n📋 Brief: {brief}\n🎨 Projekt: {design}\n⚙️ Produkcja: {production}\n📦 Dostawa: {delivery}\n\n{cta}"
    ],
    educational: [
      "💡 Czy wiesz, że...?\n\n{fact}\n\n{explanation}\n\n📚 Dowiedz się więcej: {cta}",
      "Poradnik: {title}\n\n1️⃣ {step_1}\n2️⃣ {step_2}\n3️⃣ {step_3}\n\n💡 Pro tip: {tip}\n\n{cta}",
      "FAQ: {question}\n\n{answer}\n\n🔍 Masz więcej pytań? {cta}"
    ],
    engagement: [
      "🤔 Co wolisz?\n\nA) {option_a}\nB) {option_b}\n\nZostaw reakcję! 👇",
      "Zgadnij! 🎯\n\n{riddle}\n\nOdpowiedź ujawnimy jutro! Obserwuj nas, aby nie przegapić.",
      "Oceń od 1 do 10! ⭐\n\n{item_to_rate}\n\nPodziel się swoją opinią w komentarzach! 💬"
    ],
    behind_scenes: [
      "Za kulisami 🎬\n\n{description}\n\n{fun_fact}\n\n#behindthescenes #teamwork",
      "Proces produkcji krok po kroku 🏭\n\n{process_description}\n\nCo Was najbardziej zaskoczyło? 💭",
      "Nasz zespół w akcji! 💪\n\n{team_activity}\n\n{team_quote}\n\n#teamplexisystem"
    ]
  };

  // Popularne emoji dla różnych kategorii
  private emojiLibrary = {
    success: ['🎯', '✅', '🏆', '💎', '⭐', '🌟', '✨', '🔥', '🚀', '💪'],
    product: ['📦', '🎁', '🛍️', '💼', '🏭', '⚙️', '🔧', '🔨', '🏗️', '🎨'],
    emotion: ['😊', '🤩', '😍', '🤗', '👏', '🙌', '💯', '❤️', '💙', '💚'],
    business: ['💼', '📊', '📈', '💰', '🤝', '👔', '🏢', '📋', '✍️', '📞'],
    tech: ['💻', '🖥️', '📱', '🔌', '💡', '🔬', '🔭', '🛠️', '⚡', '🔋'],
    time: ['⏰', '⏱️', '📅', '📆', '🕐', '⏳', '⌛', '🔜', '🔄', '♻️'],
    direction: ['➡️', '⬆️', '⬇️', '↗️', '🔝', '📍', '🎯', '🗺️', '🧭', '📌']
  };

  // Generuj sugestie treści - UŻYWA PRAWDZIWEGO AI API
  async generateContentSuggestions(
    topic: string,
    platforms: string[],
    tone: 'professional' | 'casual' | 'enthusiastic' | 'informative' | 'humorous' = 'professional',
    count: number = 5
  ): Promise<ContentSuggestion[]> {
    const suggestions: ContentSuggestion[] = [];
    
    try {
      // Wywołaj Universal AI Service dla generowania postów
      const API_URL = import.meta.env.VITE_API_URL || 'https://api.plexisystem.pl';
      
      const systemPrompt = `Jesteś ekspertem social media dla PlexiSystem. Generuj angażujące posty o ${topic}.
      Ton: ${tone}
      Platformy: ${platforms.join(', ')}
      
      Dla każdego posta:
      1. Treść dostosowana do platformy (Facebook max 63k znaków, LinkedIn 3000, Instagram 2200, Twitter 280)
      2. Dodaj odpowiednie emoji
      3. Zaproponuj 5-10 hashtagów
      4. Dodaj call-to-action
      5. Zachowaj ton ${tone === 'professional' ? 'profesjonalny' : tone === 'casual' ? 'swobodny' : tone === 'enthusiastic' ? 'entuzjastyczny' : tone === 'informative' ? 'informacyjny' : 'humorystyczny'}
      
      Generuj ${count} różnych wersji posta.`;
      
      const response = await fetch(`${API_URL}/api/universal-ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          module_name: 'social_media_generator',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: `Wygeneruj ${count} postów na temat: ${topic}` }
          ],
          options: {
            max_tokens: 2048,
            temperature: tone === 'professional' ? 0.3 : tone === 'casual' ? 0.6 : 0.8
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('AI API error');
      }
      
      const data = await response.json();
      let aiContent = '';
      
      // Normalizacja odpowiedzi (jak w AIAssistant)
      if (data.content && Array.isArray(data.content)) {
        const firstContent = data.content[0];
        if (firstContent?.type === 'text') {
          aiContent = firstContent.text;
        }
      } else if (typeof data.content === 'string') {
        aiContent = data.content;
      } else if (data.response) {
        aiContent = data.response;
      }
      
      // Parsuj odpowiedź AI i stwórz sugestie
      const lines = aiContent.split('\n').filter(l => l.trim());
      let currentSuggestion = '';
      
      for (const line of lines) {
        if (line.startsWith('Post ') || line.startsWith('Wersja ')) {
          if (currentSuggestion) {
            // Dodaj poprzednią sugestię
            const keywords = this.extractKeywords(currentSuggestion);
            const hashtags = await this.generateHashtags(topic, keywords, 10);
            const emojis = this.selectEmojis(this.detectCategory(topic), tone);
            
            suggestions.push({
              id: `suggestion_${Date.now()}_${suggestions.length}`,
              content: currentSuggestion,
              platforms,
              tone,
              category: this.detectCategory(topic),
              hashtags: hashtags.map(h => h.tag),
              emojis,
              engagement_score: this.calculateEngagementScore(currentSuggestion, hashtags.length, emojis.length),
              best_time_to_post: this.suggestBestTime(platforms),
              character_count: currentSuggestion.length,
              hooks: this.generateHooks(topic, tone),
              cta_suggestions: this.generateCTAs(this.detectCategory(topic))
            });
          }
          currentSuggestion = '';
        } else {
          currentSuggestion += line + '\n';
        }
      }
      
      // Dodaj ostatnią sugestię jeśli istnieje
      if (currentSuggestion) {
        const keywords = this.extractKeywords(currentSuggestion);
        const hashtags = await this.generateHashtags(topic, keywords, 10);
        const emojis = this.selectEmojis(this.detectCategory(topic), tone);
        
        suggestions.push({
          id: `suggestion_${Date.now()}_${suggestions.length}`,
          content: currentSuggestion,
          platforms,
          tone,
          category: this.detectCategory(topic),
          hashtags: hashtags.map(h => h.tag),
          emojis,
          engagement_score: this.calculateEngagementScore(currentSuggestion, hashtags.length, emojis.length),
          best_time_to_post: this.suggestBestTime(platforms),
          character_count: currentSuggestion.length,
          hooks: this.generateHooks(topic, tone),
          cta_suggestions: this.generateCTAs(this.detectCategory(topic))
        });
      }
      
    } catch (error) {
      console.error('Error calling AI API:', error);
      
      // Fallback - użyj lokalnych szablonów
      const keywords = this.extractKeywords(topic);
      const category = this.detectCategory(topic);
      
      for (let i = 0; i < count; i++) {
        const template = this.selectTemplate(category, tone);
        const content = await this.fillTemplate(template, topic, keywords);
        const hashtags = await this.generateHashtags(topic, keywords, 10);
        const emojis = this.selectEmojis(category, tone);
        const hooks = this.generateHooks(topic, tone);
        const ctas = this.generateCTAs(category);
        
        suggestions.push({
          id: `suggestion_${Date.now()}_${i}`,
          content,
          platforms,
          tone,
          category,
          hashtags: hashtags.map(h => h.tag),
          emojis,
          engagement_score: this.calculateEngagementScore(content, hashtags.length, emojis.length),
          best_time_to_post: this.suggestBestTime(platforms),
          character_count: content.length,
          hooks,
          cta_suggestions: ctas
        });
      }
    }
    
    // Sortuj po przewidywanym engagement
    suggestions.sort((a, b) => b.engagement_score - a.engagement_score);
    
    // Zapisz do bazy dla analizy
    await this.saveContentSuggestions(suggestions);
    
    return suggestions;
  }

  // Generuj hashtagi z AI
  async generateHashtags(
    topic: string,
    keywords: string[] = [],
    count: number = 30
  ): Promise<HashtagSuggestion[]> {
    const hashtags: HashtagSuggestion[] = [];
    
    // Podstawowe hashtagi branżowe
    const industryTags = this.getIndustryHashtags(topic);
    
    // Trending hashtagi
    const trendingTags = await this.getTrendingHashtags();
    
    // Lokalne hashtagi
    const localTags = this.getLocalHashtags();
    
    // Sezonowe hashtagi
    const seasonalTags = this.getSeasonalHashtags();
    
    // Mix różnych typów hashtagów
    const allTags = [
      ...industryTags.map(tag => ({ tag, type: 'industry' })),
      ...trendingTags.map(tag => ({ tag, type: 'trending' })),
      ...localTags.map(tag => ({ tag, type: 'local' })),
      ...seasonalTags.map(tag => ({ tag, type: 'seasonal' }))
    ];
    
    // Analizuj i oceniaj każdy hashtag
    for (const tagData of allTags) {
      if (hashtags.length >= count) break;
      
      const analysis = await this.analyzeHashtag(tagData.tag, topic);
      
      hashtags.push({
        tag: tagData.tag,
        relevance_score: analysis.relevance,
        popularity: analysis.popularity,
        trending: tagData.type === 'trending',
        competition_level: analysis.competition,
        estimated_reach: analysis.reach,
        related_tags: this.findRelatedHashtags(tagData.tag)
      });
    }
    
    // Sortuj według relevance i popularity
    hashtags.sort((a, b) => {
      const scoreA = a.relevance_score * (a.popularity === 'high' ? 1.5 : 1);
      const scoreB = b.relevance_score * (b.popularity === 'high' ? 1.5 : 1);
      return scoreB - scoreA;
    });
    
    return hashtags;
  }

  // Analizuj istniejącą treść
  async analyzeContent(content: string): Promise<ContentAnalysis> {
    // Analiza sentymentu
    const sentiment = this.analyzeSentiment(content);
    
    // Oblicz czytelność (Flesch Reading Ease dla polskiego)
    const readability = this.calculateReadability(content);
    
    // Przewiduj engagement
    const engagementPrediction = this.predictEngagement(content);
    
    // Sugestie ulepszeń
    const improvements = this.suggestImprovements(content);
    
    // Wyodrębnij słowa kluczowe
    const keywords = this.extractKeywords(content);
    
    return {
      sentiment,
      readability_score: readability,
      engagement_prediction: engagementPrediction,
      improvements,
      keywords
    };
  }

  // Znajdź trending topics
  async getTrendingTopics(industry: string = 'general'): Promise<TrendingTopic[]> {
    // Symulacja trending topics - w rzeczywistości można połączyć z Google Trends API
    const topics: TrendingTopic[] = [
      {
        topic: 'Ekologia i recykling tworzyw',
        volume: 15000,
        growth_rate: 45,
        related_hashtags: ['ekologia', 'recykling', 'zerowaste', 'gogreen'],
        content_ideas: [
          'Jak recyklingujemy odpady produkcyjne?',
          'Eko-alternatywy dla tradycyjnych tworzyw',
          'Nasza droga do zero waste'
        ]
      },
      {
        topic: 'Personalizacja produktów',
        volume: 8500,
        growth_rate: 32,
        related_hashtags: ['personalizacja', 'custom', 'namiare', 'unique'],
        content_ideas: [
          'Stwórz unikalny produkt dla swojej marki',
          'Proces personalizacji krok po kroku',
          'Top 10 personalizowanych produktów'
        ]
      },
      {
        topic: 'Automatyzacja produkcji',
        volume: 12000,
        growth_rate: 28,
        related_hashtags: ['automatyzacja', 'industry40', 'cnc', 'robotyka'],
        content_ideas: [
          'Jak CNC zmienia produkcję?',
          'Automatyzacja vs rękodzieło',
          'Przyszłość produkcji'
        ]
      }
    ];
    
    // Zapisz do cache
    await this.saveTrendingTopics(topics);
    
    return topics;
  }

  // Generuj content calendar
  async generateContentCalendar(
    daysAhead: number = 30,
    postsPerDay: number = 2
  ): Promise<{
    date: string;
    time: string;
    content: ContentSuggestion;
    reason: string;
  }[]> {
    const calendar = [];
    const topics = await this.getTrendingTopics();
    
    for (let day = 0; day < daysAhead; day++) {
      const date = new Date();
      date.setDate(date.getDate() + day);
      
      for (let post = 0; post < postsPerDay; post++) {
        const time = post === 0 ? '09:00' : '15:00'; // Optymalne godziny
        const topic = topics[Math.floor(Math.random() * topics.length)];
        const suggestions = await this.generateContentSuggestions(
          topic.topic,
          ['facebook', 'linkedin'],
          'professional',
          1
        );
        
        calendar.push({
          date: date.toISOString().split('T')[0],
          time,
          content: suggestions[0],
          reason: this.getPublishingReason(date, time)
        });
      }
    }
    
    return calendar;
  }

  // === Helper Functions ===

  private extractKeywords(text: string): string[] {
    // Podstawowa ekstrakcja słów kluczowych
    const stopWords = ['i', 'w', 'z', 'do', 'na', 'jest', 'to', 'że', 'czy', 'jak'];
    const words = text.toLowerCase()
      .replace(/[.,!?;]/g, '')
      .split(' ')
      .filter(word => word.length > 3 && !stopWords.includes(word));
    
    // Zlicz częstotliwość
    const frequency: Record<string, number> = {};
    words.forEach(word => {
      frequency[word] = (frequency[word] || 0) + 1;
    });
    
    // Zwróć top słowa
    return Object.entries(frequency)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([word]) => word);
  }

  private detectCategory(topic: string): string {
    const lowerTopic = topic.toLowerCase();
    
    if (lowerTopic.includes('produkt') || lowerTopic.includes('nowość')) {
      return 'product_launch';
    } else if (lowerTopic.includes('realizacja') || lowerTopic.includes('projekt')) {
      return 'case_study';
    } else if (lowerTopic.includes('poradnik') || lowerTopic.includes('jak')) {
      return 'educational';
    } else if (lowerTopic.includes('konkurs') || lowerTopic.includes('zagadka')) {
      return 'engagement';
    } else {
      return 'behind_scenes';
    }
  }

  private selectTemplate(category: string, tone: string): string {
    const templates = this.contentTemplates[category as keyof typeof this.contentTemplates] || this.contentTemplates.product_launch;
    return templates[Math.floor(Math.random() * templates.length)];
  }

  private async fillTemplate(template: string, topic: string, keywords: string[]): Promise<string> {
    // Inteligentne wypełnianie szablonu
    let content = template;
    
    const replacements: Record<string, string> = {
      '{product_name}': this.generateProductName(topic),
      '{benefit_1}': 'Najwyższa jakość wykonania',
      '{benefit_2}': 'Szybka realizacja zamówień',
      '{benefit_3}': 'Konkurencyjne ceny',
      '{cta}': this.generateCTA(),
      '{tagline}': this.generateTagline(topic),
      '{description}': this.generateDescription(topic, keywords),
      '{feature_1}': 'Precyzyjne cięcie CNC',
      '{feature_2}': 'Dowolna personalizacja',
      '{feature_3}': 'Ekologiczne materiały',
      '{client}': 'Renomowana firma z branży retail',
      '{challenge}': 'Stworzenie unikalnego systemu ekspozycji',
      '{solution}': 'Zaprojektowaliśmy modułowe standy z plexi',
      '{result}': 'Wzrost sprzedaży o 35% w pierwszym miesiącu',
      '{hook}': this.generateHook(topic),
      '{target_audience}': 'sklepy, galerie, punkty sprzedaży'
    };
    
    for (const [key, value] of Object.entries(replacements)) {
      content = content.replace(new RegExp(key, 'g'), value);
    }
    
    return content;
  }

  private generateProductName(topic: string): string {
    const names = [
      'Ekspozytor Premium',
      'Stand Modułowy Pro',
      'Display Crystal Clear',
      'Organizer Deluxe',
      'System Prezentacji 360'
    ];
    return names[Math.floor(Math.random() * names.length)];
  }

  private generateTagline(topic: string): string {
    const taglines = [
      'Perfekcja w każdym detalu',
      'Gdzie jakość spotyka innowację',
      'Rozwiązania szyte na miarę',
      'Twój pomysł, nasza realizacja',
      'Profesjonalizm na najwyższym poziomie'
    ];
    return taglines[Math.floor(Math.random() * taglines.length)];
  }

  private generateDescription(topic: string, keywords: string[]): string {
    return `Innowacyjne rozwiązanie łączące ${keywords.slice(0, 2).join(' i ')} z najnowszymi technologiami produkcji.`;
  }

  private generateHook(topic: string): string {
    const hooks = [
      'Czy zastanawiałeś się kiedyś...',
      'Odkryj sekret sukcesu...',
      'To zmieni Twoje podejście do...',
      'Oto rozwiązanie, którego szukałeś',
      'Sprawdź, dlaczego wybierają nas liderzy rynku'
    ];
    return hooks[Math.floor(Math.random() * hooks.length)];
  }

  private generateCTA(): string {
    const ctas = [
      '📞 Zadzwoń: 884 042 107',
      '💻 Sprawdź: www.plexisystem.pl',
      '✉️ Napisz do nas!',
      '🔗 Link w bio',
      '📩 Zapytaj o wycenę'
    ];
    return ctas[Math.floor(Math.random() * ctas.length)];
  }

  private generateHooks(topic: string, tone: string): string[] {
    const hooks = {
      professional: [
        'Zwiększ efektywność swojego biznesu',
        'Profesjonalne rozwiązania dla wymagających',
        'Innowacja w zasięgu ręki'
      ],
      casual: [
        'Hej! Mamy coś dla Ciebie',
        'Sprawdź to koniecznie!',
        'Nie przegap tej okazji'
      ],
      enthusiastic: [
        'WOW! To musisz zobaczyć!',
        'Niesamowite! Już dostępne!',
        'Bombowa nowość!'
      ],
      informative: [
        'Dowiedz się więcej o...',
        'Fakty, które warto znać',
        'Kompleksowy przewodnik po...'
      ],
      humorous: [
        'Uwaga, może uzależniać 😄',
        'Nie mówimy, że to magia, ale...',
        'Houston, mamy rozwiązanie!'
      ]
    };
    
    return hooks[tone] || hooks.professional;
  }

  private generateCTAs(category: string): string[] {
    return [
      'Zamów bezpłatną wycenę',
      'Skontaktuj się z nami',
      'Zobacz więcej realizacji',
      'Pobierz katalog',
      'Umów spotkanie',
      'Zapytaj o szczegóły'
    ];
  }

  private selectEmojis(category: string, tone: string): string[] {
    const emojiSets = {
      professional: [...this.emojiLibrary.business, ...this.emojiLibrary.success],
      casual: [...this.emojiLibrary.emotion, ...this.emojiLibrary.success],
      enthusiastic: [...this.emojiLibrary.emotion, ...this.emojiLibrary.success, ...this.emojiLibrary.product],
      informative: [...this.emojiLibrary.tech, ...this.emojiLibrary.direction],
      humorous: [...this.emojiLibrary.emotion]
    };
    
    const emojis = emojiSets[tone] || emojiSets.professional;
    return emojis.slice(0, 5);
  }

  private calculateEngagementScore(content: string, hashtagCount: number, emojiCount: number): number {
    let score = 50; // Base score
    
    // Długość treści (optymalna: 100-250 znaków)
    if (content.length >= 100 && content.length <= 250) score += 20;
    else if (content.length < 100) score += 10;
    else if (content.length > 400) score -= 10;
    
    // Hashtagi (optymalne: 5-10)
    if (hashtagCount >= 5 && hashtagCount <= 10) score += 15;
    else if (hashtagCount > 15) score -= 10;
    
    // Emoji (optymalne: 2-5)
    if (emojiCount >= 2 && emojiCount <= 5) score += 10;
    else if (emojiCount > 8) score -= 5;
    
    // Pytanie zwiększa engagement
    if (content.includes('?')) score += 10;
    
    // CTA zwiększa engagement
    if (content.includes('📞') || content.includes('💻') || content.includes('Link')) score += 5;
    
    return Math.min(100, Math.max(0, score));
  }

  private suggestBestTime(platforms: string[]): string {
    // Optymalne godziny publikacji dla różnych platform
    const bestTimes = {
      facebook: ['9:00', '13:00', '15:00', '19:00'],
      linkedin: ['7:30', '12:00', '17:00'],
      instagram: ['11:00', '14:00', '19:00', '21:00'],
      twitter: ['9:00', '12:00', '17:00', '20:00']
    };
    
    // Znajdź wspólną optymalną godzinę
    const commonTimes = ['9:00', '12:00', '15:00', '19:00'];
    return commonTimes[Math.floor(Math.random() * commonTimes.length)];
  }

  private getIndustryHashtags(topic: string): string[] {
    const topicLower = topic.toLowerCase();
    const tags: string[] = [];
    
    for (const [industry, keywords] of Object.entries(this.industryKeywords)) {
      if (keywords.some(keyword => topicLower.includes(keyword))) {
        tags.push(...keywords.map(k => k.replace(/\s+/g, '')));
      }
    }
    
    return [...new Set(tags)]; // Unique tags
  }

  private async getTrendingHashtags(): Promise<string[]> {
    // Symulacja trending hashtagów
    return [
      'trendy2024',
      'innowacja',
      'biznes',
      'polska',
      'ekologia',
      'design',
      'quality',
      'madeinpoland',
      'b2b',
      'startup'
    ];
  }

  private getLocalHashtags(): string[] {
    return [
      'gdansk',
      'trojmiasto',
      'pomorskie',
      'polska',
      'polskiprodukt',
      'wspieramlokalnie',
      'madeinpoland',
      'polskafirma'
    ];
  }

  private getSeasonalHashtags(): string[] {
    const month = new Date().getMonth();
    const seasonalTags: Record<number, string[]> = {
      0: ['nowyrok', 'postanowienia', 'nowypoczatek'],
      1: ['walentynki', 'love', 'prezent'],
      2: ['wiosna', 'spring', 'odnowa'],
      3: ['wielkanoc', 'easter', 'swieta'],
      4: ['maj', 'majowka', 'długiweekend'],
      5: ['lato', 'summer', 'wakacje'],
      6: ['urlop', 'vacation', 'wypoczynek'],
      7: ['sierpien', 'festival', 'wydarzenia'],
      8: ['wrzesien', 'backtowork', 'jesien'],
      9: ['halloween', 'autumn', 'październik'],
      10: ['blackfriday', 'promocje', 'listopad'],
      11: ['swieta', 'christmas', 'prezenty']
    };
    
    return seasonalTags[month] || [];
  }

  private async analyzeHashtag(
    tag: string, 
    topic: string
  ): Promise<{
    relevance: number;
    popularity: 'high' | 'medium' | 'low' | 'niche';
    competition: number;
    reach: number;
  }> {
    // Symulacja analizy hashtagu
    const relevance = Math.random() * 100;
    const popularityLevels: ('high' | 'medium' | 'low' | 'niche')[] = ['high', 'medium', 'low', 'niche'];
    const popularity = popularityLevels[Math.floor(Math.random() * popularityLevels.length)];
    const competition = Math.random() * 100;
    const reach = Math.floor(Math.random() * 50000) + 1000;
    
    return { relevance, popularity, competition, reach };
  }

  private findRelatedHashtags(tag: string): string[] {
    // Mapa powiązanych hashtagów
    const relatedMap: Record<string, string[]> = {
      'plexi': ['pleksi', 'pmma', 'akryl', 'tworzywa'],
      'ekspozytor': ['display', 'stand', 'pos', 'reklama'],
      'produkcja': ['manufacturing', 'cnc', 'factory', 'przemysl'],
      'biznes': ['b2b', 'business', 'firma', 'przedsiebiorca'],
      'design': ['projektowanie', 'wzornictwo', 'kreacja', 'art']
    };
    
    for (const [key, related] of Object.entries(relatedMap)) {
      if (tag.includes(key) || key.includes(tag)) {
        return related;
      }
    }
    
    return [];
  }

  private analyzeSentiment(content: string): 'positive' | 'negative' | 'neutral' {
    const positiveWords = ['świetny', 'super', 'najlepszy', 'wspaniały', 'sukces', 'radość', 'nowość', 'promocja'];
    const negativeWords = ['problem', 'błąd', 'zły', 'nie', 'nigdy', 'niestety', 'przykro'];
    
    const contentLower = content.toLowerCase();
    let positiveCount = 0;
    let negativeCount = 0;
    
    positiveWords.forEach(word => {
      if (contentLower.includes(word)) positiveCount++;
    });
    
    negativeWords.forEach(word => {
      if (contentLower.includes(word)) negativeCount++;
    });
    
    if (positiveCount > negativeCount) return 'positive';
    if (negativeCount > positiveCount) return 'negative';
    return 'neutral';
  }

  private calculateReadability(content: string): number {
    // Uproszczona wersja Flesch Reading Ease dla polskiego
    const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const words = content.split(/\s+/).filter(w => w.length > 0);
    const syllables = words.reduce((sum, word) => sum + this.countSyllables(word), 0);
    
    if (sentences.length === 0 || words.length === 0) return 0;
    
    const avgWordsPerSentence = words.length / sentences.length;
    const avgSyllablesPerWord = syllables / words.length;
    
    // Flesch Reading Ease (adapted for Polish)
    const score = 206.835 - 1.015 * avgWordsPerSentence - 84.6 * avgSyllablesPerWord;
    
    return Math.max(0, Math.min(100, score));
  }

  private countSyllables(word: string): number {
    // Przybliżone liczenie sylab w polskim
    const vowels = 'aąeęioóuy';
    let count = 0;
    let previousWasVowel = false;
    
    for (const char of word.toLowerCase()) {
      const isVowel = vowels.includes(char);
      if (isVowel && !previousWasVowel) {
        count++;
      }
      previousWasVowel = isVowel;
    }
    
    return Math.max(1, count);
  }

  private predictEngagement(content: string): number {
    let score = 50;
    
    // Factors that increase engagement
    if (content.includes('?')) score += 15; // Questions
    // Check for emoji using a simple approach
    const hasEmoji = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(content);
    if (hasEmoji) score += 10; // Emojis
    if (content.match(/#\w+/g)) score += 10; // Hashtags
    if (content.includes('KONKURS') || content.includes('GIVEAWAY')) score += 20;
    if (content.length > 50 && content.length < 200) score += 10; // Optimal length
    
    // Factors that decrease engagement
    if (content.length > 500) score -= 15; // Too long
    if (content.length < 20) score -= 20; // Too short
    if (!content.match(/[.!?]/)) score -= 10; // No punctuation
    
    return Math.max(0, Math.min(100, score));
  }

  private suggestImprovements(content: string): string[] {
    const improvements: string[] = [];
    
    if (!content.includes('?')) {
      improvements.push('Dodaj pytanie, aby zwiększyć zaangażowanie');
    }
    
    // Check for emoji using a simple approach
    const hasEmoji = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(content);
    if (!hasEmoji) {
      improvements.push('Użyj emoji dla lepszej wizualizacji');
    }
    
    if (!content.match(/#\w+/g)) {
      improvements.push('Dodaj hashtagi dla większego zasięgu');
    }
    
    if (content.length > 280) {
      improvements.push('Skróć treść dla lepszej czytelności');
    }
    
    if (!content.includes('📞') && !content.includes('💻') && !content.includes('www')) {
      improvements.push('Dodaj call-to-action z kontaktem');
    }
    
    const sentences = content.split(/[.!?]+/);
    if (sentences.some(s => s.split(/\s+/).length > 20)) {
      improvements.push('Podziel długie zdania na krótsze');
    }
    
    return improvements;
  }

  private getPublishingReason(date: Date, time: string): string {
    const dayOfWeek = date.getDay();
    const hour = parseInt(time.split(':')[0]);
    
    if (dayOfWeek === 1 && hour === 9) {
      return 'Poniedziałek rano - najwyższe zaangażowanie B2B';
    } else if (dayOfWeek === 5 && hour === 15) {
      return 'Piątek popołudnie - dobry czas na weekend content';
    } else if (hour === 12) {
      return 'Przerwa obiadowa - szczyt aktywności użytkowników';
    } else if (hour === 19) {
      return 'Wieczór - czas relaksu i przeglądania social media';
    }
    
    return 'Optymalny czas dla zaangażowania';
  }

  // === Database Operations ===

  private async saveContentSuggestions(suggestions: ContentSuggestion[]) {
    try {
      const { error } = await supabase
        .from('ai_content_suggestions')
        .insert(
          suggestions.map(s => ({
            content: s.content,
            platforms: s.platforms,
            tone: s.tone,
            category: s.category,
            hashtags: s.hashtags,
            engagement_score: s.engagement_score,
            created_at: new Date().toISOString()
          }))
        );
      
      if (error) {
        console.error('Error saving content suggestions:', error);
      }
    } catch (error) {
      console.error('Error in saveContentSuggestions:', error);
    }
  }

  private async saveTrendingTopics(topics: TrendingTopic[]) {
    try {
      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('trending_topics_cache')
        .upsert(
          topics.map(t => ({
            topic: t.topic,
            volume: t.volume,
            growth_rate: t.growth_rate,
            related_hashtags: t.related_hashtags,
            content_ideas: t.content_ideas,
            updated_at: new Date().toISOString()
          }))
        );
      
      if (error) {
        console.warn('Could not save trending topics (RLS issue):', error.message);
        // Continue without saving - topics are still returned
      }
    } catch (error) {
      console.warn('Error in saveTrendingTopics:', error);
      // Non-critical error - continue
    }
  }
}

// Export singleton
export const socialMediaAI = new SocialMediaAIService();
